import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Typography, Space, Drawer, Grid } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    DashboardOutlined,
    FileTextOutlined,
    ContainerOutlined,
    FileExclamationOutlined,
    HistoryOutlined,
    PayCircleOutlined,
    FileProtectOutlined,
    ShopOutlined,
    SnippetsOutlined,
    FormOutlined,
    UsergroupAddOutlined,
    AccountBookOutlined,
    RiseOutlined,
    SettingOutlined,
    ToolOutlined,
    UserOutlined,
    ClockCircleOutlined,
    LogoutOutlined,
    LineChartOutlined,
} from '@ant-design/icons';

const { Sider } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const AppSidebar = ({ user, onLogout, mobileOpen, onNavigate, sessionGraceLabel, setMobileOpen }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [collapsed, setCollapsed] = useState(false);

    // Use Ant Design's Breakpoint hook to determine mobile state independently of Sider
    const screens = useBreakpoint();
    // 'lg' is the breakpoint we used before. If 'lg' is inactive, we are on a smaller screen (mobile/tablet)
    const isMobile = !screens.lg;

    // Sync collapsed state with mobileOpen prop logic for Desktop/Sider
    // On mobile, Drawer handles visibility via 'mobileOpen' directly
    useEffect(() => {
        if (!isMobile) {
            setCollapsed(!mobileOpen);
        }
    }, [mobileOpen, isMobile]);

    const handleCollapse = (value) => {
        setCollapsed(value);
        if (setMobileOpen) {
            setMobileOpen(!value);
        }
    };

    // Helper to check permissions
    const has = (k) => user?.role === 'admin' || Boolean(user?.moduleAccess?.[k]);

    // Map current path to selected keys
    const getSelectedKeys = () => {
        const path = location.pathname;
        if (path === '/') return ['/'];
        return [path];
    };

    const getOpenKeys = () => {
        const path = location.pathname;
        if (['/invoice', '/credit-notes', '/invoice-history'].some(p => path.startsWith(p))) return ['ar'];
        if (['/payables', '/vendors'].some(p => path.startsWith(p))) return ['ap'];
        if (['/create-quote', '/quote-history'].some(p => path.startsWith(p))) return ['quotes'];
        if (['/configurations', '/users'].some(p => path.startsWith(p))) return ['settings'];
        return [];
    };

    const [openKeys, setOpenKeys] = useState(getOpenKeys());
    const rootSubmenuKeys = ['ar', 'ap', 'quotes', 'settings'];

    useEffect(() => {
        const keys = getOpenKeys();
        if (keys.length > 0) {
            setOpenKeys(keys);
        }
    }, [location.pathname]);

    const onOpenChange = (keys) => {
        const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
        if (rootSubmenuKeys.indexOf(latestOpenKey) === -1) {
            setOpenKeys(keys);
        } else {
            setOpenKeys(latestOpenKey ? [latestOpenKey] : []);
        }
    };

    const menuItems = [
        has('dashboard') && {
            key: '/dashboard',
            icon: <DashboardOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Dashboard',
            onClick: () => { navigate('/dashboard'); onNavigate && onNavigate(); }
        },
        // Account Receivable Group
        (has('createInvoice') || has('adhocInvoice') || has('invoiceHistory')) && {
            key: 'ar',
            icon: <FileTextOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Account Receivable',
            children: [
                (has('invoice') || has('createInvoice') || has('adhocInvoice')) && user?.role !== 'viewer' && {
                    key: '/invoice',
                    icon: <FileTextOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Create Invoice',
                    onClick: () => { navigate('/invoice'); onNavigate && onNavigate(); }
                },
                user?.role !== 'viewer' && {
                    key: '/credit-notes',
                    icon: <FileExclamationOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Create Credit Note',
                    onClick: () => { navigate('/credit-notes'); onNavigate && onNavigate(); }
                },
                has('invoiceHistory') && {
                    key: '/invoice-history',
                    icon: <HistoryOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'History',
                    onClick: () => { navigate('/invoice-history'); onNavigate && onNavigate(); }
                }
            ].filter(Boolean)
        },
        // Account Payable Group
        (has('payables') || has('vendors')) && {
            key: 'ap',
            icon: <PayCircleOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Account Payable',
            children: [
                has('payables') && {
                    key: '/payables',
                    icon: <FileProtectOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Payable Invoices',
                    onClick: () => { navigate('/payables'); onNavigate && onNavigate(); }
                },
                has('vendors') && {
                    key: '/vendors',
                    icon: <ShopOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Vendors',
                    onClick: () => { navigate('/vendors'); onNavigate && onNavigate(); }
                }
            ].filter(Boolean)
        },
        // Quotations Group
        (has('createQuote') || has('quoteHistory')) && {
            key: 'quotes',
            icon: <SnippetsOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Quotation',
            children: [
                has('createQuote') && user?.role !== 'viewer' && {
                    key: '/create-quote',
                    icon: <FormOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Create Quote',
                    onClick: () => { navigate('/create-quote'); onNavigate && onNavigate(); }
                },
                has('quoteHistory') && {
                    key: '/quote-history',
                    icon: <HistoryOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'History',
                    onClick: () => { navigate('/quote-history'); onNavigate && onNavigate(); }
                }
            ].filter(Boolean)
        },
        has('clients') && {
            key: '/clients',
            icon: <UsergroupAddOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Clients',
            onClick: () => { navigate('/clients'); onNavigate && onNavigate(); }
        },
        has('expenses') && {
            key: '/expenses',
            icon: <AccountBookOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Operational Cost',
            onClick: () => { navigate('/expenses'); onNavigate && onNavigate(); }
        },
        has('profitLoss') && {
            key: '/profit-loss',
            icon: <RiseOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Profit & Loss',
            onClick: () => { navigate('/profit-loss'); onNavigate && onNavigate(); }
        },
        {
            key: '/reports',
            icon: <LineChartOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Reports',
            onClick: () => { navigate('/reports'); onNavigate && onNavigate(); }
        },
        // Settings Group
        (has('configurations') || has('users')) && {
            key: 'settings',
            icon: <SettingOutlined style={{ fontSize: '1.2em' }} />,
            label: 'Settings',
            children: [
                has('configurations') && {
                    key: '/configurations',
                    icon: <ToolOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Configurations',
                    onClick: () => { navigate('/configurations'); onNavigate && onNavigate(); }
                },
                has('users') && {
                    key: '/users',
                    icon: <UserOutlined style={{ fontSize: '1.1em' }} />,
                    label: 'Users',
                    onClick: () => { navigate('/users'); onNavigate && onNavigate(); }
                }
            ].filter(Boolean)
        },

    ].filter(Boolean);

    // Reusable Content
    const sidebarContent = (
        <>
            <Menu
                mode="inline"
                selectedKeys={getSelectedKeys()}
                openKeys={openKeys}
                onOpenChange={onOpenChange}
                style={{ borderRight: 0, paddingTop: '10px' }}
                items={menuItems}
            />

            {/* Mobile Footer for User Controls */}
            {isMobile && (
                <div className="mobile-only" style={{
                    padding: '16px',
                    borderTop: '1px solid #f0f0f0',
                    marginTop: 'auto'
                }}>
                    {sessionGraceLabel && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px',
                            background: '#fff7e6',
                            border: '1px solid #ffd591',
                            borderRadius: 6,
                            color: '#d46b08',
                            fontSize: '0.85rem',
                            marginBottom: '16px'
                        }}>
                            <ClockCircleOutlined style={{ fontSize: 16 }} />
                            <Text type="warning" style={{ color: 'inherit' }}>Session: <strong>{sessionGraceLabel}</strong></Text>
                        </div>
                    )}

                    {user && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                <div style={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: '50%',
                                    background: '#667eea',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white'
                                }}>
                                    <UserOutlined style={{ fontSize: 20 }} />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Text strong style={{ fontSize: 14 }}>{user.name}</Text>
                                    <Text type="secondary" style={{ fontSize: 11 }}>{user.role === 'admin' ? 'Administrator' : 'User'}</Text>
                                </div>
                            </div>
                            <Button
                                block
                                icon={<UserOutlined style={{ fontSize: 16 }} />}
                                onClick={() => { navigate('/profile'); onNavigate && onNavigate(); }}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            >
                                My Profile
                            </Button>
                            <Button
                                danger
                                block
                                icon={<LogoutOutlined style={{ fontSize: 16 }} />}
                                onClick={onLogout}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            >
                                Sign Out
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </>
    );

    if (isMobile) {
        return (
            <Drawer
                placement="left"
                onClose={() => setMobileOpen(false)}
                open={mobileOpen}
                size={280}
                styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column' } }}
                closable={false}
            >
                {sidebarContent}
            </Drawer>
        );
    }

    return (
        <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={handleCollapse}
            width={280}
            theme="light"
            style={{
                overflow: 'auto',
                height: 'calc(100vh - 64px)',
                position: 'sticky',
                top: 64,
                borderRight: '1px solid #f0f0f0'
            }}
            breakpoint="lg"
            collapsedWidth={isMobile ? 0 : 80}
            onBreakpoint={(broken) => {
                // Warning: onBreakpoint in Sider might conflict if we rely on useBreakpoint
                // But since we render Sider only on Desktop (isMobile=false), this should rarely trigger 'broken=true'
                // unless we are exactly at the edge. 
                // We rely on useBreakpoint for the main switch.
            }}
        >
            {sidebarContent}
        </Sider>
    );
};

export default AppSidebar;
